import { useEffect, useRef, useState } from "react"
import Link from "next/link"

import { buildSofiaNotebooksPath } from "@/app-covi"
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Textarea,
} from "@coval/ui"
import { CalendarClock } from "lucide-react"

import {
  notebookRecurrenceSchema,
  type NotebookRecurrence,
} from "@/utils/notebook-recurrence"

import {
  DEFAULT_RECURRENCE,
  generativeScheduleRequest,
  type GenerativeSchedule,
} from "../utils/generativeSchedule"
import { getNotebook } from "../utils/notebooksApi"
import type { Notebook } from "../utils/notebookSnapshot"
import { NotebookRepeatFields } from "./NotebookRepeatFields"

export function NotebookScheduleButton({
  notebook,
  organizationSlug,
  workspaceId,
  currentHref,
  onNotebookReloaded,
}: {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  currentHref: string
  onNotebookReloaded: (notebook: Notebook) => void
}) {
  const [open, setOpen] = useState(false)
  const [schedule, setSchedule] = useState<GenerativeSchedule | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const [recurrence, setRecurrence] =
    useState<NotebookRecurrence>(DEFAULT_RECURRENCE)
  const [instructions, setInstructions] = useState("")
  const [attempt, setAttempt] = useState(0)
  const generation = useRef(0)
  const saving = useRef(false)
  const preserveDraft = useRef(false)
  const recurrenceValidation = notebookRecurrenceSchema.safeParse(recurrence)
  const invalidFields = new Set(
    recurrenceValidation.success
      ? []
      : recurrenceValidation.error.issues.map((issue) => issue.path[0]),
  )
  useEffect(() => {
    const current = ++generation.current
    const controller = new AbortController()
    setLoaded(false)
    setError("")
    setNotice("")
    if (!open) {
      preserveDraft.current = false
    }
    if (open) {
      void generativeScheduleRequest(
        { notebookId: notebook.id, organizationSlug, workspaceId },
        undefined,
        controller.signal,
      )
        .then((data) => {
          if (generation.current !== current) {
            return
          }
          setSchedule(data.schedule)
          if (!preserveDraft.current) {
            setRecurrence(data.schedule?.recurrence ?? DEFAULT_RECURRENCE)
            setInstructions(data.schedule?.instructions ?? "")
          }
          preserveDraft.current = false
          setLoaded(data.scheduling_available)
          if (!data.scheduling_available) {
            setError("Automatic notebook runs are unavailable here.")
          }
        })
        .catch((failure: unknown) => {
          if (generation.current === current && !controller.signal.aborted) {
            setError(
              failure instanceof Error
                ? failure.message
                : "Could not load schedule.",
            )
          }
        })
    }
    return () => {
      generation.current = current + 1
      controller.abort()
    }
  }, [open, notebook.id, organizationSlug, workspaceId, attempt])
  const save = async (enabled: boolean) => {
    if (
      !loaded ||
      saving.current ||
      (enabled && !recurrenceValidation.success)
    ) {
      return
    }
    saving.current = true
    const current = generation.current
    setPending(true)
    setError("")
    setNotice("")
    try {
      const data = await generativeScheduleRequest(
        { notebookId: notebook.id, organizationSlug, workspaceId },
        {
          recurrence: enabled
            ? recurrence
            : (schedule?.recurrence ?? recurrence),
          instructions: enabled
            ? instructions
            : (schedule?.instructions ?? instructions),
          enabled,
          expected_updated_at: schedule?.updated_at ?? null,
          expected_notebook_updated_at: notebook.updated_at,
        },
      )
      if (generation.current !== current) {
        return
      }
      setSchedule(data.schedule)
      setNotice(
        enabled
          ? "Schedule saved. Sofia will research fresh data and save each new edition in Notebooks."
          : "Schedule paused. Your notebooks are kept.",
      )
    } catch (failure: unknown) {
      if (generation.current === current) {
        setError(
          failure instanceof Error
            ? failure.message
            : "Could not save schedule.",
        )
      }
    } finally {
      saving.current = false
      if (generation.current === current) {
        setPending(false)
      }
    }
  }
  const reload = async () => {
    if (saving.current) {
      return
    }
    saving.current = true
    const current = generation.current
    const loadedBeforeReload = loaded
    setPending(true)
    setError("")
    try {
      const loaded = await getNotebook(
        organizationSlug,
        workspaceId,
        notebook.id,
      )
      if (generation.current === current) {
        onNotebookReloaded(loaded)
        preserveDraft.current ||= loadedBeforeReload
        setAttempt((value) => value + 1)
      }
    } catch (failure: unknown) {
      if (generation.current === current) {
        setError(
          failure instanceof Error
            ? failure.message
            : "Could not reload notebook.",
        )
      }
    } finally {
      saving.current = false
      if (generation.current === current) {
        setPending(false)
      }
    }
  }
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setOpen(true)}
        data-testid="notebook-schedule-button"
      >
        <CalendarClock className="size-4" /> Repeat
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!saving.current) {
            setOpen(next)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Repeat this notebook</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="text-sm text-coval-text-muted">
              Sofia repeats your original request with fresh research, keeping
              the same focus, tone and format. Each edition is saved as a new
              notebook.
            </p>
            <NotebookRepeatFields
              value={recurrence}
              onChange={setRecurrence}
              disabled={pending || !loaded}
              errors={{
                interval: invalidFields.has("interval")
                  ? "Enter a whole number from 1 to 365."
                  : undefined,
                time: invalidFields.has("time")
                  ? "Choose a valid 24-hour time."
                  : undefined,
                timezone: invalidFields.has("timezone")
                  ? "Enter a valid IANA timezone, such as America/Los_Angeles."
                  : undefined,
              }}
            />
            <FormField
              label="Instructions for future editions"
              description="Optional. Keep specific sections, change the lookback, or focus on a particular trend."
            >
              {(ids) => (
                <Textarea
                  {...ids}
                  value={instructions}
                  maxLength={4000}
                  disabled={pending || !loaded}
                  onChange={(event) => setInstructions(event.target.value)}
                />
              )}
            </FormField>
            {schedule && (
              <p className="text-sm" role="status">
                {schedule.enabled
                  ? `Next edition: ${schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleString("en-US", { timeZone: schedule.recurrence.timezone, timeZoneName: "short" }) : "Pending"}`
                  : "Paused"}
              </p>
            )}
            {schedule?.last_run?.error && (
              <p className="text-sm text-coval-danger-text">
                Last run: {schedule.last_run.error}
              </p>
            )}
            {schedule?.last_run?.notebook_id && (
              <Link
                className="text-sm text-coval-info-text underline"
                href={buildSofiaNotebooksPath(
                  currentHref,
                  organizationSlug,
                  schedule.last_run.notebook_id,
                )}
              >
                Open latest edition
              </Link>
            )}
            {error && (
              <div
                role="alert"
                className="space-y-2 text-sm text-coval-danger-text"
              >
                <p>{error}</p>
                <Button
                  variant="secondary"
                  disabled={pending}
                  onClick={() => void reload()}
                >
                  Reload notebook and schedule
                </Button>
              </div>
            )}
            {notice && (
              <p role="status" className="text-sm text-coval-success-text">
                {notice}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              disabled={pending}
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
            {schedule?.enabled && (
              <Button
                variant="secondary"
                disabled={pending || !loaded}
                onClick={() => void save(false)}
              >
                Pause
              </Button>
            )}
            <Button
              disabled={pending || !loaded || !recurrenceValidation.success}
              loading={pending}
              onClick={() => void save(true)}
            >
              {schedule?.enabled ? "Save schedule" : "Schedule notebook"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
